import type { FrequencyType } from 'apiConnector/scheduledRun/model';
import classnames from 'classnames';
import { AmountWithUnit } from 'components/shared/Amount/Amount';
import { DateRangePicker } from 'components/shared/DatePicker/DatesRangePicker';
import { VerticalFormGroup } from 'components/shared/FormGroup';
import { ValidationFeedbackMessage } from 'components/shared/FormGroup/ErrorMessages';
import type { WithIncrementersOptions } from 'components/shared/Percent/WithIncrementers';
import { withIncrementers } from 'components/shared/Percent/WithIncrementers';
import type { IdOwnProps } from 'components/shared/ScheduledRun/type';
import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, type MapDispatchToPropsFunction, type MapStateToProps } from 'react-redux';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import type { ScheduledRunFields } from 'state/scheduledRuns/state';
import type { AppState } from 'state/state.model';
import { getTodayStringInLocalTimezone } from 'utils/core/date';

interface StateProps {
  startDate?: string;
  endDate?: string;
  frequency: FrequencyType;
  frequencyTime?: number;
}
interface DispatchProps {
  onDateRangeChanged: (startDate?: string, endDate?: string) => void;
  onFrequencyChanged: (frequency: FrequencyType) => void;
  onFrequencyTimeChanged: (frequencyTime?: number) => void;
}

interface OwnProps extends IdOwnProps {
  disabled?: boolean;
}
interface Props extends StateProps, DispatchProps, OwnProps {}

const mapStateToProps: MapStateToProps<StateProps, OwnProps, AppState> = ({ scheduledRun }, { id }) => ({
  frequency: scheduledRun.runs[id].frequency.type,
  frequencyTime: scheduledRun.runs[id].frequency.value,
  startDate: scheduledRun.runs[id].range?.startDate,
  endDate: scheduledRun.runs[id].range?.endDate,
});
const mapDispatchToProps: MapDispatchToPropsFunction<DispatchProps, OwnProps> = (dispatch, { id }) => ({
  onDateRangeChanged(startDate, endDate) {
    dispatch(scheduledRunActions.datesRangeChanged(id, startDate, endDate));
  },
  onFrequencyChanged(frequency) {
    dispatch(scheduledRunActions.frequencyChanged(id, frequency));
  },
  onFrequencyTimeChanged(frequencyTime) {
    dispatch(scheduledRunActions.frequencyTimeChanged(id, frequencyTime));
  },
});
const frequencyKeys: Record<string, FrequencyType> = {
  M: 'Months',
  W: 'Weeks',
  B: 'BusinessDays',
  D: 'Days',
};
export const ConnectedScheduleRange = connect(mapStateToProps, mapDispatchToProps)(ScheduleRange);
const frequencyOptions: WithIncrementersOptions = {
  layout: 'end',
  endClassName: 'input-group-text p-0',
  triggerType: 'change',
};
function ScheduleRange({
  startDate,
  endDate,
  frequency,
  frequencyTime,
  onDateRangeChanged,
  onFrequencyChanged,
  onFrequencyTimeChanged,
  disabled,
}: Props) {
  const { formatMessage } = useIntl();
  const Frequency = withIncrementers(AmountWithUnit, frequencyOptions);
  const handleKey = useCallback(
    (event: KeyboardEvent<Element>) => {
      const frequency = frequencyKeys[event.key.toUpperCase()];
      if (frequency) {
        onFrequencyChanged(frequency);
      }
    },
    [onFrequencyChanged],
  );
  return (
    <>
      <VerticalFormGroup<keyof ScheduledRunFields> label="scheduledRun.scheduleRange" inputId="range" errorPrefix="">
        <DateRangePicker
          id={'dateRange'}
          closeWhenClickOutside
          disabled={disabled}
          allowWeekend={false}
          startDate={startDate}
          minDate={getTodayStringInLocalTimezone()}
          startPlaceholder={formatMessage({ id: 'scheduledRun.scheduleRange.startDate' })}
          endPlaceholder={formatMessage({ id: 'scheduledRun.scheduleRange.endDate' })}
          endDate={endDate}
          onChange={range => onDateRangeChanged(range.startDate, range.endDate)}
        />
        <ValidationFeedbackMessage validationError={undefined} />
      </VerticalFormGroup>
      <VerticalFormGroup label="scheduledRun.frequency" inputId="frequency" errorPrefix="">
        <Frequency
          onChange={onFrequencyTimeChanged}
          isFocused
          isDisabled={disabled}
          accelerators={[]}
          className={classnames('form-control')}
          e2e="frequency"
          incrementStep={1}
          precision={0}
          decimalPadding={false}
          onKeyPress={handleKey}
          value={frequencyTime}
          placeholder={formatMessage({ id: 'scheduledRun.frequency.placeholder' })}
          unit={formatMessage({ id: `scheduledRun.frequency.${frequency}` })}
          unitE2e={`${frequency}-unit`}
        >
          <span className="input-group-text">
            <FormattedMessage id="scheduledRun.frequency.prepend" />
          </span>
        </Frequency>
      </VerticalFormGroup>
    </>
  );
}
